// DOM side of the Inventor-style measure results panel.
import type { DistanceMode, MeasureResult, MeasureValue } from './types';
import { announce, dom, formatAngleDeg, formatDistance, isPanelOpen, setPanelOpen } from './ui';

let onModeChange: ((mode: DistanceMode) => void) | null = null;
let onValueSelect: ((valueId: string) => void) | null = null;
let wired = false;

export function setMeasureModeChangeHandler(fn: (mode: DistanceMode) => void): void {
  onModeChange = fn;
  ensureWired();
}

/** Called when a result row is clicked (after copy). Used to preview ΔX/Y/Z in the scene. */
export function setMeasureValueSelectHandler(fn: (valueId: string) => void): void {
  onValueSelect = fn;
  ensureWired();
}

function ensureWired(): void {
  if (wired) return;
  wired = true;
  dom.measureModeRow.addEventListener('click', e => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.measure-mode-btn');
    const mode = btn?.dataset.mode as DistanceMode | undefined;
    if (!mode || !onModeChange) return;
    onModeChange(mode);
  });
  const onPick = (e: Event): void => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-copy]');
    if (!el) return;
    copyFrom(el);
  };
  dom.measureValues.addEventListener('click', onPick);
  dom.measureComponents.addEventListener('click', onPick);
  dom.measurePrimary.addEventListener('click', onPick);
  dom.measureHelpBtn.addEventListener('click', () => {
    const show = dom.measureHelp.classList.toggle('hidden') === false;
    dom.measureHelpBtn.setAttribute('aria-expanded', show ? 'true' : 'false');
  });
}

/** Copy what the element holds, tell the scene which value is now on show. */
function copyFrom(el: HTMLElement): void {
  const text = el.dataset.copy ?? '';
  const valueId = el.dataset.valueId ?? '';
  if (valueId && onValueSelect) onValueSelect(valueId);
  void navigator.clipboard?.writeText(text).then(() => {
    el.classList.add('copied');
    announce('Copied: ' + text);
    setTimeout(() => el.classList.remove('copied'), 900);
  }).catch(() => {
    announce('Copy failed');
  });
}

/** Updates which result row is highlighted as the active display. */
export function setMeasurePanelPrimary(valueId: string | null): void {
  dom.measureValues.querySelectorAll<HTMLLIElement>('li[data-value-id]').forEach(li => {
    li.classList.toggle('primary', !!valueId && li.dataset.valueId === valueId);
  });
  dom.measureComponents.querySelectorAll<HTMLButtonElement>('button[data-value-id]').forEach(btn => {
    btn.classList.toggle('primary', !!valueId && btn.dataset.valueId === valueId);
  });
}

export function setMeasurePanelOpen(open: boolean): void {
  setPanelOpen(dom.measurePanel, dom.measureBtn, open);
}

export function isMeasurePanelOpen(): boolean {
  return isPanelOpen(dom.measurePanel);
}

export function setMeasureStatus(text: string): void {
  dom.measureStatus.textContent = text;
}

function formatValue(v: MeasureValue): string {
  return v.unit === 'deg' ? formatAngleDeg(v.value) : formatDistance(v.value);
}

export function renderMeasurePanel(result: MeasureResult | null, mode: DistanceMode, primaryId: string | null): void {
  ensureWired();
  if (!result) {
    dom.measureTitle.classList.add('hidden');
    dom.measureTitle.textContent = '';
    dom.measurePrimary.classList.add('hidden');
    dom.measureComponents.classList.add('hidden');
    dom.measureComponents.innerHTML = '';
    dom.measureModeRow.classList.add('hidden');
    dom.measureValues.innerHTML = '';
    return;
  }

  // What was measured, and whether the two entities run parallel - that belongs
  // here rather than on the model, where it would only crowd the dimension.
  dom.measureTitle.textContent = result.title;
  // Parallel only says something when both entities have a direction; two
  // vertices are never parallel to each other.
  if (result.isParallel && result.showAngle) {
    const chip = document.createElement('span');
    chip.className = 'measure-parallel';
    chip.textContent = 'parallel';
    dom.measureTitle.appendChild(chip);
  }
  dom.measureTitle.classList.remove('hidden');

  const modes = result.distanceModes;
  if (modes.length > 0) {
    dom.measureModeRow.classList.remove('hidden');
    dom.measureModeRow.querySelectorAll<HTMLButtonElement>('.measure-mode-btn').forEach(btn => {
      const m = btn.dataset.mode as DistanceMode;
      btn.classList.toggle('active', m === mode);
      btn.style.display = modes.includes(m) ? '' : 'none';
    });
  } else {
    dom.measureModeRow.classList.add('hidden');
  }

  // The headline number: the value the model is showing, big enough to read
  // from across the desk.
  const primary = result.values.find(v => v.id === primaryId) ?? result.values[0];
  if (primary) {
    const formatted = formatValue(primary);
    dom.measurePrimaryValue.textContent = formatted;
    dom.measurePrimary.dataset.copy = formatted;
    dom.measurePrimary.dataset.valueId = primary.id;
    dom.measurePrimary.title = primary.label + ' — click to copy';
    dom.measurePrimary.classList.remove('hidden');
  } else {
    dom.measurePrimary.classList.add('hidden');
  }

  // ΔX / ΔY / ΔZ side by side: three numbers that are read together.
  const axisIds = ['dx', 'dy', 'dz'];
  const axisValues = result.values.filter(v => axisIds.includes(v.id));
  dom.measureComponents.innerHTML = '';
  if (axisValues.length > 0) {
    for (const v of axisValues) {
      const formatted = formatValue(v);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.dataset.copy = formatted;
      btn.dataset.valueId = v.id;
      btn.title = 'Show in model and copy';
      btn.classList.toggle('primary', v.id === primaryId);
      const label = document.createElement('span');
      label.className = 'mc-label';
      label.textContent = v.label;
      const value = document.createElement('span');
      value.className = 'mc-value';
      value.textContent = formatted;
      btn.append(label, value);
      dom.measureComponents.appendChild(btn);
    }
    dom.measureComponents.classList.remove('hidden');
  } else {
    dom.measureComponents.classList.add('hidden');
  }

  // Everything else stays a list: the other distances, and the angle.
  dom.measureValues.innerHTML = '';
  result.values.forEach(v => {
    if (axisIds.includes(v.id) || v.id === primary?.id) return;
    const li = document.createElement('li');
    const formatted = formatValue(v);
    li.dataset.copy = formatted;
    li.dataset.valueId = v.id;
    li.title = v.id === 'xyz' ? 'Show in model and copy' : 'Copy value';
    const label = document.createElement('span');
    label.className = 'mv-label';
    label.textContent = v.label;
    const value = document.createElement('span');
    value.className = 'mv-value';
    value.textContent = formatted;
    li.append(label, value);
    dom.measureValues.appendChild(li);
  });
}

export function primaryValueIdForMode(mode: DistanceMode, result: MeasureResult): string {
  if (result.distanceModes.includes(mode)) {
    if (mode === 'min') return result.values.some(v => v.id === 'min') ? 'min' : 'perp';
    if (mode === 'max') return 'max';
    return result.values.some(v => v.id === 'center') ? 'center' : 'perp';
  }
  return result.values.find(v => v.id === 'perp' || v.id === 'center' || v.id === 'xyz')?.id ?? result.values[0]?.id ?? '';
}

// ---------------------------------------------------------------------------
// Measurement list (all measurements; the active one shows its details below)
// ---------------------------------------------------------------------------

export interface MeasureListItem {
  id: number;
  title: string;
  value: string;
  visible: boolean;
  active: boolean;
}

export interface MeasureListHandlers {
  select(id: number): void;
  toggleVisible(id: number): void;
  remove(id: number): void;
  clearAll(): void;
}

const ICON_EYE = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" /> <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" /></svg>';
const ICON_EYE_OFF = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10.585 10.587a2 2 0 0 0 2.829 2.828" /> <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87" /> <path d="M3 3l18 18" /></svg>';
const ICON_TRASH = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7l16 0" /> <path d="M10 11l0 6" /> <path d="M14 11l0 6" /> <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" /> <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" /></svg>';

let listHandlers: MeasureListHandlers | null = null;

export function setMeasureListHandlers(handlers: MeasureListHandlers): void {
  if (!listHandlers) {
    dom.measureClearAllBtn.addEventListener('click', () => listHandlers?.clearAll());
  }
  listHandlers = handlers;
}

export function renderMeasureList(items: MeasureListItem[]): void {
  dom.measureList.classList.toggle('hidden', items.length === 0);
  dom.measureListItems.replaceChildren();
  for (const item of items) {
    const li = document.createElement('li');
    li.className = 'ml-row' + (item.active ? ' active' : '') + (item.visible ? '' : ' off');
    li.title = 'Show details';

    const no = document.createElement('span');
    no.className = 'ml-no';
    no.textContent = '#' + item.id;
    const title = document.createElement('span');
    title.className = 'ml-title';
    title.textContent = item.title;
    const value = document.createElement('span');
    value.className = 'ml-value';
    value.textContent = item.value;

    const eye = document.createElement('button');
    eye.type = 'button';
    eye.className = 'ml-act' + (item.visible ? '' : ' on');
    eye.title = item.visible ? 'Hide in model' : 'Show in model';
    eye.setAttribute('aria-label', eye.title + ' #' + item.id);
    eye.innerHTML = item.visible ? ICON_EYE : ICON_EYE_OFF;
    eye.addEventListener('click', e => { e.stopPropagation(); listHandlers?.toggleVisible(item.id); });

    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'ml-act danger';
    del.title = 'Delete';
    del.setAttribute('aria-label', 'Delete measurement #' + item.id);
    del.innerHTML = ICON_TRASH;
    del.addEventListener('click', e => { e.stopPropagation(); listHandlers?.remove(item.id); });

    li.addEventListener('click', () => listHandlers?.select(item.id));
    li.append(no, title, value, eye, del);
    dom.measureListItems.appendChild(li);
  }
}
